<template>
	<div class="alert-container">
		<transition-group name="list" class="alert-list" tag="div">
			<div
				class="alert-item"
				:class="{ error: alert.error, warning: alert.warning }"
				v-for="alert in alerts"
				:key="alert.id"
				ref="alertItems"
			>
				<div class="alert-icon" v-if="alert.icon">
					<component :is="alert.icon" />
				</div>
				<div
					class="alert-text"
					:class="{
						'alert-center-text': alert.subText,
						'alert-text-close': alert.allowClose,
					}"
				>
					<div v-if="alert.allowClose" class="alert-close" @click="hide(alert)">
						<CloseIcon color="white" />
					</div>
					{{ alert.text }}
					<div v-if="alert.subText" class="alert-sub-text">
						{{ alert.subText }}
					</div>
				</div>
			</div>
		</transition-group>
	</div>
</template>

<script>
import { CloseIcon } from '@/icons'
import { markRaw, reactive } from 'vue'

const DEFAULT_TIME = 5000
const alerts = reactive({})

const hashCode = (s) =>
	s.split('').reduce((a, b) => {
		a = (a << 5) - a + b.charCodeAt(0)
		return a & a
	}, 0)

// TODO: tweak main Alert component to handle meetings-style
// alerts and then deprecate this one
export const showAlert = ({
	text,
	subText,
	icon,
	error,
	warning,
	allowClose,
	autoHide = true,
	time = DEFAULT_TIME,
}) => {
	if (!text) return
	const id = hashCode(text)
	const previousAlert = alerts[id]
	if (previousAlert) {
		// cancel timeout if it was pending
		clearTimeout(previousAlert.timeout)
	}
	const newAlert = { id, text, subText, error, warning, allowClose }
	if (icon) {
		newAlert.icon = markRaw(icon)
	}
	if (autoHide) {
		newAlert.timeout = setTimeout(() => hideAlert(id), time)
	}
	alerts[id] = newAlert
	return id
}

export const hideAlert = (id) => {
	const alert = alerts[id]
	if (!alert) return
	clearTimeout(alert.timeout) // cancel timeout if it was pending
	delete alerts[id]
}

export default {
	name: 'MeetingAlert',
	components: { CloseIcon },
	data() {
		return { alerts }
	},
	methods: {
		hide(alert) {
			hideAlert(alert.id)
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';
$error: #ff525a;
$warn: #f9bb22;

.alert-container {
	display: flex;
	justify-content: center;
	align-items: flex-start;
	z-index: 2;
}
.alert-list {
	position: absolute;
	margin-top: 8px;
	z-index: 10;
	display: flex;
	flex-direction: column;
	align-items: center;
}
.alert-item {
	opacity: 1;
	max-height: 80px;
	position: relative;
	display: flex;
	align-items: center;
	color: white;
	background: black;
	border: 1px solid var(--teal-300);
	border-radius: 10px;
	padding: 8px 16px;
	margin-bottom: 16px;
	overflow: hidden;
	&.error {
		background: $error;
		border-color: $error;
	}
	&.warning {
		background: $warn;
		border-color: $warn;
	}
	.alert-icon {
		width: 25px;
		height: 25px;
		margin-right: 8px;
	}
	&:last-child {
		margin-bottom: 0;
	}
	.alert-close {
		position: absolute;
		top: 8px;
		right: 15px;
		cursor: pointer;
		svg {
			width: 10px;
			height: 10px;
		}
	}
}
.alert-sub-text {
	font-family: 'Avenir-Heavy';
	font-size: 12px;
	margin-top: 7px;
}
.alert-center-text {
	text-align: center;
}
.alert-text-close {
	margin-right: 25px;
}
.list-enter-active,
.list-leave-active {
	transition: all ease-out 0.3s;
	transition-property: max-height, opacity;
}
.list-enter,
.list-leave-to {
	opacity: 0;
	max-height: 0;
}
:deep(.alert-icon) svg {
	height: 25px;
}
</style>
